import { ConfigServiceProvider } from '../../../core/services/config-service/config-service';
import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { UserTask } from '../../interfaces/user-task.interface';
import { Observable } from 'rxjs';
import { FormVariable, FormVariables } from '../../../../submodules/sellions_forms/projects/forms/src/lib/interfaces/form-variables.interface';
import { FormDataService } from '../../../../submodules/sellions_forms/projects/forms/src/lib/services/form-data/form-data.service';
import { TaskResponse } from '../../interfaces/task-response.interface';
import { switchMap, delay, retryWhen, catchError, mergeMap, tap, take, map } from 'rxjs/operators';
import { StorageServiceProvider } from '../../../core/services/storage-service/storage-service';
import { ToastController } from 'ionic-angular';
import { MultipartFile } from '../../../../submodules/sellions_forms/projects/forms/src/lib/components/controls/multipart-files/multipart-file';
import { FormField } from '../../../../submodules/sellions_forms/projects/forms/src/lib/interfaces/form-field.interface';

@Injectable()
export class UserTaskService {
  readonly userTaskStorePrefix = 'user-task-form-';
  readonly userTasksFormsStoredKeysStoreKey = 'userTasksFormsStoreKeys';
  readonly delay_between_tries = 1000;
  readonly sending_retries = 3;
  readonly pendingUserTaskFormPrefix = 'pending-user-task-';
  readonly pendingUserTasksKeysStoreKey = 'pendingUserTasksFormsStoreKeys';

  private pendingUserTaskFormsKeys: string[] = [];
  private storedUserTaskForm: string[] = [];

  public reloadUserTaskListEventEmitter = new EventEmitter();

  constructor(
    private configService: ConfigServiceProvider,
    private http: HttpClient,
    private formDataService: FormDataService,
    private toastController: ToastController,
    private storageService: StorageServiceProvider,
  ) {
    this.getPendingUserTaskFormKeysFromStorage();
  }

  public get(userTaskId: string): Observable<UserTask> {
    const userTaskUrl = this.getUrl(userTaskId, 'userTask');
    return this.http.get<UserTask>(userTaskUrl).pipe(
      tap((res) => this.storeUserTask(userTaskId, res)),
      catchError(() => this.getUserTaskFormFromStorage(userTaskId)),
      mergeMap((userTask) => this.getPendingUserTaskDataIfExists(userTaskId, userTask)),
    );
  }

  private storeUserTask(userTaskId: string, userTask: UserTask): void {
    const userTaskFormStoreKey = this.getUserTaskFormStoreKey(userTaskId);
    this.storageService.setObject(this.userTaskStorePrefix + userTaskId, userTask).then(() => this.storeUserTaskFormKey(userTaskFormStoreKey));
  }

  private storeUserTaskFormKey(userTaskFormStoreKey: string): void {
    const isKeyAlreadyStored = !!this.storedUserTaskForm.find((existingKey) => existingKey === userTaskFormStoreKey);

    if (!isKeyAlreadyStored) {
      this.storedUserTaskForm.push(userTaskFormStoreKey);
      this.storageService.setObject(this.userTasksFormsStoredKeysStoreKey, this.storedUserTaskForm);
    }
  }

  private getUserTaskFormFromStorage(userTaskId: string): Observable<UserTask> {
    return Observable.from(this.storageService.getObject(this.getUserTaskFormStoreKey(userTaskId))).pipe(
      tap(
        () => this.showToastMessage('Wystąpił problem z Twoim połączeniem internetowym. Wyświetlono zadanie z pamięci aplikacji'),
        () => this.showToastMessage('Wystąpił problem z Twoim połączeniem internetowym. Nie można wyświetlić zadania'),
      ),
    );
  }

  private getUserTaskFormStoreKey(userTaskId: string): string {
    return `${this.userTaskStorePrefix}${userTaskId}`;
  }

  private getPendingUserTaskDataIfExists(userTaskId: string, userTask: UserTask): Observable<UserTask> {
    const userTaskStoreKey = this.getUserTaskStoreKey(userTaskId);

    return Observable.from(this.getPendingUserTaskFormWithId(userTaskStoreKey)).pipe(
      map((pendingTaskData) => this.setUserTaskVariables(pendingTaskData, userTask)),
      catchError(() => Observable.of(userTask)),
    );
  }

  private setUserTaskVariables(
    pendingTaskData: { userTaskStoreKey: string; userTaskId: string; variables: FormVariables },
    userTask: UserTask,
  ): UserTask {
    const form = JSON.parse(userTask.form) as FormField[];

    pendingTaskData.variables.variables.forEach((variable) => {
      if (this.isFileVariable(variable, form)) {
        const multipartFiles = this.getMultipartFiles(variable.value, pendingTaskData.variables.multipartFiles);
        userTask.variables[variable.name] = multipartFiles;
      } else {
        userTask.variables[variable.name] = variable.value;
      }
    });

    return userTask;
  }

  private isFileVariable(variable: FormVariable, form: FormField[]): boolean {
    const formField = form.find((field) => field.id === variable.name);
    return formField && formField.type === 'multipartFiles' && variable.value && Array.isArray(variable.value) && variable.value.length > 0;
  }

  private getMultipartFiles(filePaths: string[], multipartFiles: MultipartFile[]): MultipartFile[] {
    const files = [];

    filePaths.forEach((path) => {
      const file = multipartFiles.find((file) => file.fileNameWithPath === path);
      if (file) {
        files.push(file);
      }
    });

    return files;
  }

  public claim(userTaskId: string): Observable<TaskResponse> {
    const userTaskUrl = this.getUrl(userTaskId, 'claimUserTask');
    return this.http.post<TaskResponse>(userTaskUrl, {}).pipe(take(1));
  }

  public complete(userTaskId: string, formVariables: FormVariables): Observable<TaskResponse> {
    const formData = this.formDataService.mapVariablesToFormData(formVariables);
    const userTaskUrl = this.getUrl(userTaskId, 'completeUserTask');

    let retries = this.sending_retries;

    return this.http.post<TaskResponse>(userTaskUrl, formData).pipe(
      retryWhen((errors) => this.handleRetries(errors, retries)),
      tap(() => this.removeProcessFormFromPending(this.getUserTaskStoreKey(userTaskId))),
      catchError((error) => this.storeUserTaskFormOnLackOfConnection(error, userTaskId, formVariables)),
    );
  }

  private getUrl(userTaskId: string, endpoint: string): string {
    return this.configService.getEngineUrl(endpoint).replace('{id}', userTaskId);
  }

  private handleRetries(errors: Observable<any>, retries: number): Observable<TaskResponse> {
    return errors.pipe(
      switchMap((error) => {
        if (error.status === 0 && retries-- > 0) {
          const remainingTries = this.sending_retries - retries;
          this.showToastMessage(`Brak połączenia z internetem. Próba przesłania formularza: ${remainingTries} z ${this.sending_retries}`);
          return Observable.of(error).pipe(delay(this.delay_between_tries));
        }

        return Observable.throw(error);
      }),
    );
  }

  private storeUserTaskFormOnLackOfConnection(error: any, userTaskId: string, formVariables: FormVariables): Observable<any> {
    if (error.status === 0) {
      this.showToastMessage('Wystąpił problem z Twoim połączeniem internetowym. Formularz zadania został zachowany do późniejszego przesłania');

      const files: File[] = [];

      formVariables.multipartFiles.forEach((multipartFile) => {
        files.push(multipartFile.file);
        multipartFile.file = null; // PouchDB handles files/blobs by attachments
      });

      const key = this.getUserTaskStoreKey(userTaskId);

      this.storageService.setObjectWithAttachments(key, formVariables, files).then(() => this.storePendingUserTaskKey(key));
    }

    return Observable.throw(error);
  }

  private getUserTaskStoreKey(userTaskId: string): string {
    return this.pendingUserTaskFormPrefix + userTaskId;
  }

  private storePendingUserTaskKey(userTaskKey: string): void {
    const isKeyAlreadyStored = !!this.pendingUserTaskFormsKeys.find((existingKey) => existingKey === userTaskKey);

    if (!isKeyAlreadyStored) {
      this.pendingUserTaskFormsKeys.push(userTaskKey);
      this.storageService.setObject(this.pendingUserTasksKeysStoreKey, this.pendingUserTaskFormsKeys);
    }
  }

  public getCountOfPendingUserTaskForms(): number {
    return this.pendingUserTaskFormsKeys.length;
  }

  public trySendPendingProcessForms(): Observable<TaskResponse> {
    this.showToastMessage('Rozpoczęto próbę ponownego przesłania oczekujących formularzy zadań');

    return Observable.from(this.getPendingUserTaskFormKeysFromStorage()).pipe(
      mergeMap((values) => Observable.from(values)),
      mergeMap((userTaskStoreKey) => Observable.from(this.getPendingUserTaskFormWithId(userTaskStoreKey))),
      mergeMap((formWithKey) => this.completePendingUserTask(formWithKey)),
      tap(() => this.showPendingUserTaskSendingMessage()),
    );
  }

  private completePendingUserTask(formWithKey: { userTaskStoreKey: string; userTaskId: string; variables: FormVariables }): Observable<TaskResponse> {
    const formData = this.formDataService.mapVariablesToFormData(formWithKey.variables);
    const userTaskUrl = this.getUrl(formWithKey.userTaskId, 'completeUserTask');

    return this.http.post<TaskResponse>(userTaskUrl, formData).pipe(
      tap(
        () => this.removeProcessFormFromPending(formWithKey.userTaskStoreKey),
        () => this.showToastMessage('Wystąpił błąd podczas próby'),
      ),
    );
  }

  private showPendingUserTaskSendingMessage(): void {
    const msg = this.getPendingUserTaskSendingMessage();
    this.showToastMessage(`Przesłano oczekujący formularz zadania. ${msg}`);
  }

  private getPendingUserTaskSendingMessage(): string {
    if (this.pendingUserTaskFormsKeys.length) {
      return `Pozostało: ${this.pendingUserTaskFormsKeys.length}`;
    }
    return 'Wszystkie oczekujące formularze zostały przesłane';
  }

  private getPendingUserTaskFormKeysFromStorage(): Promise<string[]> {
    return this.storageService.getObject(this.pendingUserTasksKeysStoreKey).then(
      (val) => {
        this.pendingUserTaskFormsKeys = val;
        return this.pendingUserTaskFormsKeys;
      },
      () => {
        this.pendingUserTaskFormsKeys = [];
        return this.pendingUserTaskFormsKeys;
      },
    );
  }

  private getPendingUserTaskFormWithId(
    userTaskStoreKey: string,
  ): Promise<{ userTaskStoreKey: string; userTaskId: string; variables: FormVariables }> {
    return this.storageService.getObjectWithAttachments<FormVariables>(userTaskStoreKey).then((result) => {
      const processWithVariables = result.object;

      processWithVariables.multipartFiles.forEach((multipartFile) => {
        const fileName = MultipartFile.getFileNameWithoutPath(multipartFile);
        const attachment = result.attachments[fileName];
        multipartFile.file = new File([attachment.data], fileName, { type: attachment.content_type });
      });

      const userTaskId = this.getUserTaskIdFromStoreKey(userTaskStoreKey);

      return { userTaskStoreKey, userTaskId, variables: processWithVariables };
    });
  }

  private getUserTaskIdFromStoreKey(userTaskStoreKey: string): string {
    return userTaskStoreKey.substring(this.pendingUserTaskFormPrefix.length);
  }

  private removeProcessFormFromPending(userTaskStoreKey: string): void {
    const index = this.pendingUserTaskFormsKeys.indexOf(userTaskStoreKey);
    if (index > -1) {
      this.pendingUserTaskFormsKeys.splice(index, 1);
      this.storageService
        .setObject(this.pendingUserTasksKeysStoreKey, this.pendingUserTaskFormsKeys)
        .then(() => this.storageService.remove(userTaskStoreKey));
    }
  }

  private showToastMessage(message: string): void {
    this.toastController
      .create({
        message,
        position: 'top',
        duration: 5000,
      })
      .present();
  }

  public removeUserData(): void {
    this.storedUserTaskForm.forEach((key) => this.storageService.remove(key));
    this.storageService.remove(this.userTasksFormsStoredKeysStoreKey).then(() => (this.storedUserTaskForm = []));

    this.pendingUserTaskFormsKeys.forEach((key) => this.storageService.remove(key));
    this.storageService.remove(this.pendingUserTasksKeysStoreKey).then(() => (this.pendingUserTaskFormsKeys = []));
  }
}



// WEBPACK FOOTER //
// ./src/modules/user-tasks/services/user-task.service/user-task.service.ts